import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { usePublicLanguage } from '../contexts/PublicLanguageContext';
import PublicLanguageSelector from './PublicLanguageSelector';
import brandLogo from '../assets/brand/logo.png';

const PublicHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = usePublicLanguage();

  const navigation = [
    { name: t('navigation.home'), href: '/' },
    { name: t('navigation.features'), href: '/fonctionnalites' },
    { name: t('navigation.contact'), href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50 public-nav-hand-header">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src={brandLogo}
              alt="Candio logo"
              className="h-10 w-10 object-contain rounded-xl shadow-sm"
            />
            <div className="flex flex-col">
              <span className="text-xl font-black text-gray-900 tracking-tight public-nav-brand-title">Candio</span>
              <span className="text-xs text-gray-500 font-medium -mt-0.5 public-nav-brand-slogan">{t('brand.slogan')}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 text-sm font-medium transition-colors duration-200 public-nav-link ${
                  isActive(item.href)
                    ? 'text-primary-600 border-b-2 border-primary-600 public-nav-link-active'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons & Language Selector */}
          <div className="hidden md:flex items-center space-x-4">
            <PublicLanguageSelector />
            <Link
              to="/login"
              className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-200 public-nav-auth-link"
            >
              {t('navigation.login')}
            </Link>
            <Link
              to="/signup"
              className="btn-primary public-nav-signup-btn"
            >
              {t('navigation.signup')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 public-nav-mobile-toggle"
              aria-label={isMenuOpen ? t('common.close') : t('common.menu')}
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-gray-200"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 public-nav-mobile-link ${
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50 public-nav-mobile-link-active'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 pb-2 border-t border-gray-200">
                <div className="px-3 py-2">
                  <PublicLanguageSelector showLabel={true} />
                </div>
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md public-nav-mobile-auth-link"
                >
                  {t('navigation.login')}
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMenuOpen(false)}
                  className="block mx-3 mt-2 px-3 py-2 text-base font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 text-center public-nav-mobile-signup-btn"
                >
                  {t('navigation.signup')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

export default PublicHeader;
